/**
 * SellEquityModal — lets a player sell back a percentage of a runner holding.
 *
 * Props:
 *   runnerId      {string}  — runner ID (key used in EntryContext allocations)
 *   runnerName    {string}  — runner name (key used in Firestore race_allocations)
 *   currentEquity {number}  — player's current holding %
 *   currentCost   {number}  — total amount paid for that holding
 *   slateId       {string}
 *   raceId        {string}
 *   onClose       {Function} — called when the modal is dismissed (no sale)
 *   onSold        {Function} — called after a successful sale
 */

import React, { useState, useMemo } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useEntryState } from '../context/EntryContext';

const COMMISSION_RATE = 0.10; // 10% seller's commission

export const SellEquityModal = ({
  runnerId,
  runnerName,
  currentEquity,
  currentCost,
  slateId,
  raceId,
  isRaceLocked = false,
  onClose,
  onSold,
}) => {
  const [sellPercent, setSellPercent] = useState(0);
  const [status,      setStatus]      = useState('idle'); // idle | loading | success | error
  const [errorMsg,    setErrorMsg]    = useState('');

  // Read race spend + floor from EntryContext for the floor-warning check,
  // and updateRunnerEquity to sync local context after a successful sale.
  const { complianceSummary, perRaceFloor, updateRunnerEquity } = useEntryState();
  const raceCompliance   = complianceSummary[raceId] || {};
  const currentRaceSpend = raceCompliance.totalSpentOnRace || 0;

  // ── Live preview calculations ──────────────────────────────────────────────
  const preview = useMemo(() => {
    if (sellPercent <= 0 || currentEquity <= 0) {
      return { costOfSoldPortion: 0, refund: 0, commission: 0, retainedEquity: currentEquity };
    }
    const proportion      = sellPercent / currentEquity;
    const costOfSold      = Math.round(currentCost * proportion);
    const refund          = Math.round(costOfSold * (1 - COMMISSION_RATE));
    const commission      = costOfSold - refund;
    const retainedEquity  = currentEquity - sellPercent;
    return { costOfSoldPortion: costOfSold, refund, commission, retainedEquity };
  }, [sellPercent, currentEquity, currentCost]);

  // Floor warning — will this sale put the race below the minimum spend?
  const willBeUnderFloor = useMemo(() => {
    if (!perRaceFloor || sellPercent <= 0) return false;
    const afterSaleSpend = currentRaceSpend - preview.costOfSoldPortion;
    return afterSaleSpend < perRaceFloor;
  }, [perRaceFloor, currentRaceSpend, preview.costOfSoldPortion, sellPercent]);

  // ── Confirm sale ────────────────────────────────────────────────────────────
  const handleConfirm = async () => {
    if (sellPercent <= 0 || status === 'loading' || isRaceLocked) return;
    setStatus('loading');
    setErrorMsg('');
    try {
      const fn = httpsCallable(
        getFunctions(undefined, 'australia-southeast1'),
        'sellRunnerEquity'
      );
      // Guarantee an integer — slider step=1 should always produce one, but guard
      // against float currentEquity bleeding through from a previous partial sell.
      const sellPercentInt = Math.round(sellPercent);
      console.log('[SellEquity] Calling with:', {
        slateId,
        raceId,
        runnerName,
        sellPercent,
        sellPercentInt,
        sellPercentType: typeof sellPercent,
        isInteger: Number.isInteger(sellPercent),
      });
      const result = await fn({ slateId, raceId, runnerId, runnerName, sellPercent: sellPercentInt });
      const newEquity = result.data?.newEquity ?? preview.retainedEquity;
      const newCost   = currentCost - preview.costOfSoldPortion;

      // Sync EntryContext immediately — compliance gate and wallet re-evaluate
      // without waiting for a page refresh (there is no live Firestore listener
      // on the player entry, so this optimistic update is the only trigger).
      updateRunnerEquity(raceId, runnerId, newEquity, Math.max(0, newCost));

      setStatus('success');
      setTimeout(() => {
        onSold?.();
        onClose();
      }, 1500);
    } catch (err) {
      setStatus('error');
      setErrorMsg(err.message || 'Sale failed. Please try again.');
    }
  };

  // ── Styles ──────────────────────────────────────────────────────────────────
  const overlay = {
    position:        'fixed',
    inset:           0,
    background:      'rgba(2,8,20,0.80)',
    backdropFilter:  'blur(4px)',
    zIndex:          3000,
    display:         'flex',
    alignItems:      'center',
    justifyContent:  'center',
    padding:         '20px',
  };

  const card = {
    background:   '#0D1829',
    border:       '1px solid rgba(239,68,68,0.35)',
    borderRadius: '12px',
    padding:      '28px 28px 24px',
    width:        '100%',
    maxWidth:     '420px',
    position:     'relative',
    color:        '#F1F5F9',
  };

  return (
    <div style={overlay} onClick={onClose}>
      <div style={card} onClick={e => e.stopPropagation()}>

        {/* ── Close button ─────────────────────────────────────────── */}
        <button
          onClick={onClose}
          style={{
            position: 'absolute', top: 14, right: 14,
            background: 'none', border: 'none',
            color: '#6B7280', fontSize: 18, cursor: 'pointer',
          }}
          aria-label="Close"
        >
          ✕
        </button>

        {/* ── Header ───────────────────────────────────────────────── */}
        <h3 style={{ margin: '0 0 4px', fontFamily: 'inherit', fontSize: 16, fontWeight: 700, color: '#fff' }}>
          Sell Equity — {runnerName.toUpperCase()}
        </h3>
        <p style={{ margin: '0 0 20px', fontSize: 13, color: '#9CA3AF' }}>
          You hold <strong style={{ color: '#F1F5F9' }}>{currentEquity}%</strong>
          {' '}— paid{' '}
          <strong style={{ color: '#F1F5F9' }}>
            ${currentCost.toLocaleString('en-AU')}
          </strong>
        </p>

        {/* ── Slider ───────────────────────────────────────────────── */}
        <label style={{ fontSize: 12, color: '#9CA3AF', display: 'block', marginBottom: 6 }}>
          Sell percentage (1% steps)
        </label>
        <input
          type="range"
          min={0}
          max={currentEquity}
          step={1}
          value={sellPercent}
          onChange={e => { setSellPercent(Math.round(Number(e.target.value))); setStatus('idle'); setErrorMsg(''); }}
          disabled={status === 'loading' || status === 'success'}
          style={{ width: '100%', accentColor: '#EF4444', marginBottom: 8 }}
        />
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#6B7280', marginBottom: 16 }}>
          <span>0%</span>
          <span style={{ color: sellPercent > 0 ? '#EF4444' : '#6B7280', fontWeight: 700, fontSize: 14 }}>
            Selling {sellPercent}%
          </span>
          <span>{currentEquity}%</span>
        </div>

        {/* ── Live preview ──────────────────────────────────────────── */}
        {sellPercent > 0 && (
          <div style={{
            background:   'rgba(255,255,255,0.04)',
            border:       '1px solid rgba(255,255,255,0.08)',
            borderRadius: 8,
            padding:      '14px 16px',
            marginBottom: 14,
            fontSize:     13,
            display:      'flex',
            flexDirection:'column',
            gap:          6,
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: '#9CA3AF' }}>Refund (90%):</span>
              <span style={{ color: '#4ADE80', fontWeight: 700 }}>
                +${preview.refund.toLocaleString('en-AU')}
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: '#9CA3AF' }}>Commission (10%):</span>
              <span style={{ color: '#F87171' }}>
                −${preview.commission.toLocaleString('en-AU')}
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: 6 }}>
              <span style={{ color: '#9CA3AF' }}>You will retain:</span>
              <span style={{ color: '#F1F5F9', fontWeight: 600 }}>
                {preview.retainedEquity}%
                {preview.retainedEquity === 0 && (
                  <span style={{ color: '#6B7280', fontWeight: 400, fontSize: 11 }}> (full exit)</span>
                )}
              </span>
            </div>
          </div>
        )}

        {/* ── Race locked warning ───────────────────────────────────── */}
        {isRaceLocked && (
          <div style={{
            background:   'rgba(239,68,68,0.10)',
            border:       '1px solid #EF4444',
            borderRadius: 8,
            padding:      '10px 14px',
            marginBottom: 14,
            fontSize:     12,
            color:        '#FCA5A5',
            fontWeight:   600,
          }}>
            🔒 This race has locked — no buying, selling, or transferring is allowed after jump time.
          </div>
        )}

        {/* ── Floor warning ─────────────────────────────────────────── */}
        {willBeUnderFloor && (
          <div style={{
            background:   'rgba(245,158,11,0.10)',
            border:       '1px solid #F59E0B',
            borderRadius: 8,
            padding:      '10px 14px',
            marginBottom: 14,
            fontSize:     12,
            color:        '#FCD34D',
            lineHeight:   1.5,
          }}>
            ⚠ This race will be below the minimum spend after this sale. You'll need to buy more
            equity in this race before you can submit your entry.
          </div>
        )}

        {/* ── Success state ─────────────────────────────────────────── */}
        {status === 'success' && (
          <div style={{
            background:   'rgba(34,197,94,0.10)',
            border:       '1px solid #22c55e',
            borderRadius: 8,
            padding:      '12px 14px',
            marginBottom: 14,
            fontSize:     13,
            color:        '#4ADE80',
            textAlign:    'center',
            fontWeight:   600,
          }}>
            ✓ Sold! Your wallet has been updated.
          </div>
        )}

        {/* ── Error state ───────────────────────────────────────────── */}
        {status === 'error' && (
          <div style={{
            background:   'rgba(239,68,68,0.10)',
            border:       '1px solid #EF4444',
            borderRadius: 8,
            padding:      '10px 14px',
            marginBottom: 14,
            fontSize:     12,
            color:        '#FCA5A5',
          }}>
            ⚠ {errorMsg}
          </div>
        )}

        {/* ── Buttons ───────────────────────────────────────────────── */}
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button
            onClick={onClose}
            disabled={status === 'loading'}
            style={{
              padding:      '9px 20px',
              background:   'transparent',
              border:       '1px solid rgba(255,255,255,0.15)',
              borderRadius: 6,
              color:        '#9CA3AF',
              fontSize:     13,
              cursor:       status === 'loading' ? 'not-allowed' : 'pointer',
              transition:   'border-color 0.15s',
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={sellPercent <= 0 || status === 'loading' || status === 'success' || isRaceLocked}
            style={{
              padding:      '9px 20px',
              background:   sellPercent > 0 && status !== 'loading' && status !== 'success'
                ? '#EF4444'
                : 'rgba(239,68,68,0.3)',
              border:       'none',
              borderRadius: 6,
              color:        '#fff',
              fontSize:     13,
              fontWeight:   600,
              cursor:       sellPercent <= 0 || status === 'loading' || status === 'success'
                ? 'not-allowed'
                : 'pointer',
              transition:   'background 0.15s',
              minWidth:     100,
            }}
          >
            {status === 'loading'
              ? '⟳ Selling…'
              : status === 'success'
              ? '✓ Done'
              : 'Confirm Sale'}
          </button>
        </div>

      </div>
    </div>
  );
};

export default SellEquityModal;
